/**
 * HR Files and Letters Page — two-tab view.
 *
 * Files tab: metadata view of uploaded documents with category filter + delete.
 * HR Letters tab: request new letters + view past requests with download.
 */

import * as React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@yesbos/ui';
import {
  CheckCircle2,
  Download,
  FileText,
  Folder,
  Trash2,
} from 'lucide-react';
import { hrosApi } from '@/lib/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface HRFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size_kb?: number;
  category: string;
  storage_url?: string;
  is_verified: boolean;
  expiry_date?: string;
  created_at: string;
}

interface HRLetter {
  id: string;
  letter_type: string;
  reason?: string;
  status: 'pending' | 'processing' | 'ready' | 'delivered' | 'rejected' | string;
  generated_url?: string;
  processed_at?: string;
  created_at: string;
  employee_name?: string;
}

interface BLLResponse {
  success: boolean;
  data?: unknown;
  error?: { message: string };
}

// ── BLL helpers ───────────────────────────────────────────────────────────────

async function callBLL(
  path: string,
  method = 'GET',
  body?: unknown,
): Promise<BLLResponse> {
  switch (method.toUpperCase()) {
    case 'POST': return hrosApi.post(path, body) as Promise<BLLResponse>;
    case 'PUT': return hrosApi.put(path, body) as Promise<BLLResponse>;
    case 'DELETE': return hrosApi.delete(path) as Promise<BLLResponse>;
    default: return hrosApi.get(path) as Promise<BLLResponse>;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const FILE_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'identity', label: 'Identity' },
  { value: 'education', label: 'Education' },
  { value: 'experience', label: 'Experience' },
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'contract', label: 'Contract' },
  { value: 'payslip', label: 'Payslip' },
  { value: 'other', label: 'Other' },
];

const LETTER_TYPES = [
  { value: 'experience', label: 'Experience Certificate' },
  { value: 'bonafide', label: 'Bonafide Certificate' },
  { value: 'salary_certificate', label: 'Salary Certificate' },
  { value: 'offer', label: 'Offer Letter' },
  { value: 'noc', label: 'No Objection Certificate (NOC)' },
  { value: 'other', label: 'Other' },
];

const LETTER_STATUS_CLASS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  processing: 'bg-blue-100 text-blue-700 border-blue-200',
  ready: 'bg-green-100 text-green-700 border-green-200',
  delivered: 'bg-teal-100 text-teal-700 border-teal-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

// ── Utility ───────────────────────────────────────────────────────────────────

function formatFileSize(kb?: number): string {
  if (kb == null) return '';
  if (kb < 1024) return `${kb} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function isExpiringSoon(dateStr?: string): boolean {
  if (!dateStr) return false;
  const diff = new Date(dateStr).getTime() - Date.now();
  return diff > 0 && diff < 30 * 24 * 60 * 60 * 1000; // within 30 days
}

function isExpired(dateStr?: string): boolean {
  if (!dateStr) return false;
  return new Date(dateStr).getTime() < Date.now();
}

// ── Files Tab ─────────────────────────────────────────────────────────────────

function FilesTab() {
  const [files, setFiles] = React.useState<HRFile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const fetchFiles = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await callBLL('/v1/hros/files');
      if (res.success && Array.isArray(res.data)) {
        setFiles(res.data as HRFile[]);
      } else {
        setError(res.error?.message ?? 'Failed to load files.');
      }
    } catch {
      setError('Network error loading files.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      const res = await callBLL(`/v1/hros/files/${id}`, 'DELETE');
      if (res.success) {
        setToast({ message: 'File deleted successfully.', type: 'success' });
        await fetchFiles();
      } else {
        setToast({
          message: res.error?.message ?? 'Failed to delete file.',
          type: 'error',
        });
      }
    } catch {
      setToast({ message: 'Network error. Please try again.', type: 'error' });
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }

  const filtered = React.useMemo(
    () =>
      categoryFilter === 'all'
        ? files
        : files.filter((f) => f.category === categoryFilter),
    [files, categoryFilter],
  );

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Delete confirm dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-base font-semibold mb-2">Delete File</h3>
            <p className="text-sm text-muted-foreground mb-4">
              This action cannot be undone. Are you sure you want to delete this
              file?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deletingId === confirmDeleteId}
                onClick={() => handleDelete(confirmDeleteId)}
              >
                {deletingId === confirmDeleteId ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Category filter */}
      <div className="flex items-center gap-3">
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            {FILE_CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">
          {filtered.length} file{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Note about upload */}
      <p className="text-xs text-muted-foreground italic">
        File uploads are handled via the AxonFiles service. This page displays
        document metadata.
      </p>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-28 rounded-lg border bg-muted animate-pulse"
            />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Folder className="h-8 w-8" />}
          title="No Files Found"
          description={
            categoryFilter !== 'all'
              ? 'No files in this category.'
              : 'No documents have been uploaded yet.'
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((file) => {
            const expired = isExpired(file.expiry_date);
            const expiringSoon = !expired && isExpiringSoon(file.expiry_date);

            return (
              <Card
                key={file.id}
                className="hover:shadow-md transition-shadow"
              >
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {file.file_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {file.file_type.toUpperCase()}
                          {file.file_size_kb != null &&
                            ` · ${formatFileSize(file.file_size_kb)}`}
                        </p>
                      </div>
                    </div>
                    {file.is_verified && (
                      <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge variant="outline" className="text-xs">
                      {file.category}
                    </Badge>
                    {(expired || expiringSoon) && (
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full border ${
                          expired
                            ? 'bg-red-50 text-red-700 border-red-200'
                            : 'bg-orange-50 text-orange-700 border-orange-200'
                        }`}
                      >
                        {expired
                          ? 'Expired'
                          : `Expires ${new Date(file.expiry_date!).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Added{' '}
                    {new Date(file.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>

                  <div className="flex justify-end mt-3">
                    <button
                      onClick={() => setConfirmDeleteId(file.id)}
                      className="text-xs text-destructive hover:underline flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── HR Letters Tab ────────────────────────────────────────────────────────────

function HRLettersTab() {
  const [letters, setLetters] = React.useState<HRLetter[]>([]);
  const [loadingLetters, setLoadingLetters] = React.useState(true);
  const [letterError, setLetterError] = React.useState('');

  // Request form state
  const [letterType, setLetterType] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState('');
  const [toast, setToast] = React.useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const fetchLetters = React.useCallback(async () => {
    setLoadingLetters(true);
    try {
      const res = await callBLL('/v1/hros/hr-letters');
      if (res.success && Array.isArray(res.data)) {
        setLetters(res.data as HRLetter[]);
      } else {
        setLetterError(res.error?.message ?? 'Failed to load letters.');
      }
    } catch {
      setLetterError('Network error loading letters.');
    } finally {
      setLoadingLetters(false);
    }
  }, []);

  React.useEffect(() => {
    fetchLetters();
  }, [fetchLetters]);

  React.useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  async function handleSubmitRequest(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!letterType) {
      setFormError('Please select a letter type.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await callBLL('/v1/hros/hr-letters', 'POST', {
        letter_type: letterType,
        reason: reason.trim() || undefined,
      });
      if (res.success) {
        setToast({ message: 'Letter request submitted successfully.', type: 'success' });
        setLetterType('');
        setReason('');
        await fetchLetters();
      } else {
        setFormError(res.error?.message ?? 'Failed to submit request.');
      }
    } catch {
      setFormError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const letterTypeLabel = (type: string): string =>
    LETTER_TYPES.find((t) => t.value === type)?.label ?? type;

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg ${
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Request Form */}
      <Card>
        <CardContent className="pt-5">
          <h3 className="text-base font-semibold mb-4">Request a Letter</h3>
          <form onSubmit={handleSubmitRequest} noValidate>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1.5">
                  Letter Type <span className="text-destructive">*</span>
                </label>
                <Select value={letterType} onValueChange={setLetterType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select letter type…" />
                  </SelectTrigger>
                  <SelectContent>
                    {LETTER_TYPES.map((lt) => (
                      <SelectItem key={lt.value} value={lt.value}>
                        {lt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium block mb-1.5">
                  Reason{' '}
                  <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Purpose or reason for requesting this letter…"
                  rows={3}
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>

              {formError && (
                <p className="text-sm text-destructive">{formError}</p>
              )}

              <Button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Request Letter'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Past Requests */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
          Past Requests
        </h3>

        {loadingLetters ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-20 rounded-lg border bg-muted animate-pulse"
              />
            ))}
          </div>
        ) : letterError ? (
          <p className="text-sm text-destructive">{letterError}</p>
        ) : letters.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-8 w-8" />}
            title="No Letter Requests"
            description="Your HR letter requests will appear here."
          />
        ) : (
          <div className="space-y-3">
            {letters.map((letter) => {
              const canDownload =
                (letter.status === 'ready' || letter.status === 'delivered') &&
                letter.generated_url;
              const statusClass =
                LETTER_STATUS_CLASS[letter.status] ??
                'bg-gray-100 text-gray-600 border-gray-200';

              return (
                <Card key={letter.id}>
                  <CardContent className="pt-3 pb-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-sm font-medium">
                            {letterTypeLabel(letter.letter_type)}
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${statusClass}`}
                          >
                            {letter.status}
                          </span>
                        </div>
                        {letter.employee_name && (
                          <p className="text-xs text-muted-foreground">
                            {letter.employee_name}
                          </p>
                        )}
                        {letter.reason && (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {letter.reason}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          Requested{' '}
                          {new Date(letter.created_at).toLocaleDateString(
                            'en-IN',
                            {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            },
                          )}
                          {letter.processed_at && (
                            <>
                              {' · '}Processed{' '}
                              {new Date(letter.processed_at).toLocaleDateString(
                                'en-IN',
                                { day: 'numeric', month: 'short', year: 'numeric' },
                              )}
                            </>
                          )}
                        </p>
                      </div>

                      {canDownload && (
                        <a
                          href={letter.generated_url!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function FilesPage() {
  const [activeTab, setActiveTab] = React.useState('files');

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Files &amp; Letters</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Manage your HR documents and request official letters.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="files">My Files</TabsTrigger>
          <TabsTrigger value="letters">HR Letters</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="mt-4">
          <FilesTab />
        </TabsContent>

        <TabsContent value="letters" className="mt-4">
          <HRLettersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default FilesPage;
